import {
  Box,
  Grid,
  Typography,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import { useUpdateCandidate } from "../../../hooks/tenants/useUpdateCandidate";
import { CandidatePayload, CandidatePayloadProxy } from "../../../model/tenant";
import {
  pets as petOptions,
} from "../../../utils/radioValues";
import {
  adultsInHousehold,
  kidsInHousehold,
  rentalPeriod,
} from "../../../utils/selectValues";
import AdvancedInput from "../../elements/AdvancedInput";
import { useTranslation } from "react-i18next";
import SavingDataSnackbar from "../../common/SavingDataSnackbar";

type GeneralInfoProps = {
  property: any;
  candidate: any;
  setTriggers: any;
  setGeneralInfoComplete: (complete: boolean) => void;
};

const GeneralInfosForm = ({
  property,
  candidate,
  setTriggers,
  setGeneralInfoComplete,
}: GeneralInfoProps) => {
  const { t } = useTranslation();
  const [showRentalPeriod, setShowRentalPeriod] = useState<boolean>(false);
  const [hasPets, setHasPets] = useState<boolean>(candidate?.pets);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const { update: updateCandidate, loading } = useUpdateCandidate();
  const {
    control,
    getValues,
    formState: { errors, isDirty },
    setValue,
    trigger,
    watch,
  } = useForm<CandidatePayload>({
    mode: 'onChange'
  });

  // Watch all required fields
  const introduction = watch('introduction');
  const sharedIncome = watch('sharedIncome');
  const rentalPeriodValue = watch('rentalPeriod');
  const rentingDate = watch('rentingDate');
  const amountOfAdults = watch('amountOfAdults');
  const amountOfKids = watch('amountOfKids');
  const pets = watch('pets');
  const rentingDateExplanation = watch('rentingDateExplanation');
  const viewing = watch('viewing');

  useEffect(() => {
    // Check if all required fields are filled
    const isFormValid = Boolean(
      introduction && 
      sharedIncome && 
      rentalPeriodValue && 
      rentingDate && 
      amountOfAdults && 
      amountOfKids !== undefined && 
      pets !== undefined &&
      (!showRentalPeriod || (showRentalPeriod && rentingDateExplanation)) &&
      Object.keys(errors).length === 0
    );

    setGeneralInfoComplete(isFormValid);
  }, [
    introduction,
    sharedIncome,
    rentalPeriodValue,
    rentingDate,
    amountOfAdults,
    amountOfKids,
    pets,
    rentingDateExplanation,
    showRentalPeriod,
    errors,
    setGeneralInfoComplete
  ]);

  useEffect(() => {
    setTriggers((triggers: any) => [...triggers, trigger]);
  }, [setTriggers, trigger]);

  const debouncedUpdateCandidate = useDebouncedCallback(async () => {
    if (isDirty) {
      const data = payloadTypeConversions(getValues());
      await updateCandidate(data);
    }
  }, 2000);

  const onInputChange = () => {
    debouncedUpdateCandidate();
  };

  const payloadTypeConversions = (data: any) => {
    return {
      ...data,
      candidateId: candidate.id,
      amountOfGuarantors: Number(data.amountOfGuarantors),
      sharedIncome: Number(data.sharedIncome),
      amountOfSigners: Number(data.amountOfSigners),
      amountOfRenters: Number(data.amountOfRenters),
      viewing: Boolean(data.viewing),
      videoViewing: Boolean(data.videoViewing),
      pets: hasPets,
      review: +data.review,
    };
  };

  useEffect(() => {
    if (candidate) {
      setValue("bidPrice", candidate.bidPrice);
      setValue("review", candidate.review);
      setValue("introduction", candidate.introduction);
      setValue("rentingDate", candidate.rentingDate);
      setValue("rentalPeriod", candidate.rentalPeriod);
      setValue("sharedIncome", candidate.sharedIncome || "");
      setValue("amountOfRenters", candidate.amountOfRenters);
      setValue("amountOfSigners", candidate.amountOfSigners);
      setValue("pets", hasPets);
      setValue("viewing", Boolean(candidate.viewing));
      setValue("videoViewing", Boolean(candidate.videoViewing));
      setValue("rentingDateExplanation", candidate.rentingDateExplanation);
      setValue("bidPriceExplanation", candidate.bidPriceExplanation);
      setValue("amountOfAdults", Number(candidate.amountOfAdults));
      setValue("amountOfKids", candidate.amountOfKids);
      setValue("petsDetails", candidate.petsDetails);

      setShowRentalPeriod(
        rentalPeriod.find((e) => e.id === candidate.rentalPeriod)?.other ||
          false,
      );
    }
  }, [candidate, hasPets]);

  const handleShowRentalPeriod = (event: any) => {
    const selectedOption = rentalPeriod.find(
      (e) => e.id === event.target.value,
    );
    setShowRentalPeriod(!!selectedOption?.other);
  };

  return (
    <>
      <Grid container>
          <Box marginTop={1}>
            <Grid
              container
              rowSpacing={2.5}
              columnSpacing={2.5}
              display={"flex"}
            >
              {/* Introduction */}
              <Grid item xs={12} sm={8} md={12}>
                <Typography variant="h4" marginBottom={2}>
                  {t('screening.general_info.title')}
                </Typography>

                <AdvancedInput
                  type="multiline"
                  label={t('screening.general_info.motivation')}
                  helperText={null}
                  name={CandidatePayloadProxy.introduction}
                  control={control}
                  changeValue={onInputChange}
                  customWidth="100%"
                  errors={errors}
                  rules={{ required: true }}
                />
              </Grid>
              {/* Shared Income */}
              <Grid item xs={12} sm={12} md={12}>
                <AdvancedInput
                  type="number"
                  label={t('screening.general_info.monthly_income')}
                  rules={{ required: true, onlyNumbers: true, min: 3 }}
                  helperText={t('screening.general_info.monthly_income_help')}
                  name={CandidatePayloadProxy.sharedIncome}
                  control={control}
                  errors={errors}
                  changeValue={onInputChange}
                />
              </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  {/* Viewing Options */}
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {t('screening.general_info.viewing_type')}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <AdvancedInput
                        type="checkbox"
                        name={CandidatePayloadProxy.viewing}
                        control={control}
                        changeValue={onInputChange}
                        label={t('screening.dropdowns.viewing_options.on_location')}
                      />
                      <AdvancedInput
                        type="checkbox"
                        name={CandidatePayloadProxy.videoViewing}
                        control={control}
                        changeValue={onInputChange}
                        label={t('screening.dropdowns.viewing_options.video')}
                      />
                    </Stack>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  {viewing &&
                    <Box marginLeft={1} marginBottom={1}>
                      <Typography variant="body2">
                        {t('screening.general_info.viewing_rating')}
                      </Typography>
                      <Box sx={{ marginTop: 2 }}>
                        <AdvancedInput
                          type="stars"
                          label="Beoordeling"
                          name={CandidatePayloadProxy.review}
                          control={control}
                          changeValue={onInputChange}
                        />
                      </Box>
                    </Box>
                  }
                </Grid>

              <Grid item xs={12} sm={8} md={6}>
                {/* Rental period */}
                <AdvancedInput
                  type="select"
                  helperText={null}
                  name={CandidatePayloadProxy.rentalPeriod}
                  control={control}
                  changeValue={(e: any) => {
                    onInputChange();
                    handleShowRentalPeriod(e);
                  }}
                  rules={{ required: true }}
                  errors={errors}
                  label={t('screening.general_info.rental_period')}
                  selectValues={rentalPeriod.map(option => ({
                    ...option,
                    name: t(option.name)
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={8} md={6}>
                {/* Renting date*/}
                <AdvancedInput
                  type="date"
                  label={t('screening.general_info.rental_date')}
                  name={CandidatePayloadProxy.rentingDate}
                  control={control}
                  changeValue={onInputChange}
                  customWidth="100%"
                  rules={{ required: true }}
                  errors={errors}
                />
              </Grid>
              {showRentalPeriod && (
                <Grid item xs={12} sm={12} md={12}>
                  <AdvancedInput
                    type="text"
                    label={t('screening.general_info.rental_date_explanation')}
                    name={CandidatePayloadProxy.rentingDateExplanation}
                    control={control}
                    changeValue={onInputChange}
                    customWidth="100%"
                    rules={{ required: true }}
                    errors={errors}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={8} md={12}>
                <Typography variant="h4">{t('screening.general_info.composition')}</Typography>
              </Grid>
              <Grid item xs={12} sm={8} md={4}>
                <AdvancedInput
                  type="select"
                  helperText={null}
                  name={CandidatePayloadProxy.amountOfAdults}
                  control={control}
                  changeValue={(e: any) => {
                    onInputChange();
                  }}
                  rules={{ required: true }}
                  errors={errors}
                  label={t('screening.general_info.adults')}
                  selectValues={adultsInHousehold}
                />
              </Grid>
              <Grid item xs={12} sm={8} md={4}>
                <AdvancedInput
                  type="select"
                  helperText={null}
                  name={CandidatePayloadProxy.amountOfKids}
                  control={control}
                  changeValue={(e: any) => {
                    onInputChange();
                  }}
                  label={t('screening.general_info.kids')}
                  selectValues={kidsInHousehold}
                />
              </Grid>
              <Grid item xs={12} sm={8} md={4}>
                <AdvancedInput
                  type="select2"
                  helperText={null}
                  label={t('screening.general_info.pets')}
                  name={CandidatePayloadProxy.pets}
                  customValue={hasPets ? 1 : 2}
                  control={control}
                  changeValue={(e: any) => {
                    onInputChange();
                    setHasPets(e.target.value === 1 ? true : false);
                  }}
                  selectValues={petOptions.map(option => ({
                    ...option,
                    name: t(option.name)
                  }))}
                />
              </Grid>

              {hasPets && (
                <Grid item xs={12} sm={8} md={12}>
                  <AdvancedInput
                    type="text"
                    label={t('screening.general_info.pets_details')}
                    name={CandidatePayloadProxy.petsDetails}
                    control={control}
                    changeValue={onInputChange}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            padding={2}
          ></Box>
      </Grid>
      <SavingDataSnackbar
        open={openSnackbar}
        loading={loading}
        setOpen={setOpenSnackbar}
      />
    </>
  );
};

export default GeneralInfosForm;
